// Encadré du BUREAU sur la fiche d'un devis (décision du dirigeant du 31/10/2026, migration 051) : ajouts / déductions constatés sur les
// bons d'intervention SIGNÉS liés à ce devis (lignes figées à la signature et notes chiffrées ajoutées après), avec le total net.
// Rien n'est ajouté au devis : le bureau décide (avenant au devis ou facture). Jamais affiché au technicien (droit devis.couts, l'API
// répond 403 de toute façon) ni sans l'option « Bons d'intervention ».
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { LIBELLES_SENS_AJUSTEMENT, formatMontantSigne, type SensAjustement } from '@shared/bons-intervention.ts';
import { api } from '../../lib/api.ts';
import { date, pluriel } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useModulesEtat } from '../../lib/modules.ts';
import { Panneau } from '../../components/ui.tsx';
import '../../styles/bons.css';

interface LigneConstatee {
  bon_id: string; reference: string; version_no: number; le: string; origine: 'bon' | 'note';
  sens: SensAjustement; raison: string; montant_ht: string; centimes: number;
}
interface Constates { bons: number; lignes: LigneConstatee[]; total_net_centimes: number }

/** Affiché sur un devis accepté, ou dès qu'un bon signé le cite. */
export function EncadreAjustementsDevis({ quoteId, accepte }: { quoteId: string; accepte: boolean }) {
  const { peut } = useSession();
  const { charge, modules } = useModulesEtat();
  const actif = charge && !!modules.bons_intervention && peut('bons.lire') && peut('devis.couts');
  const q = useQuery({ queryKey: ['bons-ajustements-devis', quoteId], enabled: actif, retry: false,
    queryFn: () => api.get<Constates>(`/api/bons/devis/${quoteId}/ajustements`) });
  if (!actif || !q.data || (!accepte && !q.data.bons)) return null;
  const c = q.data;
  return (
    <Panneau titre="Ajouts / déductions constatés sur les bons d’intervention" className="bi-encadre-devis">
      <div className="pile">
        <p className="petit secondaire">
          Rien n’est ajouté automatiquement au devis : le bureau décide de la suite (avenant au devis ou facture).
          {' '}Seuls comptent les bons signés liés à ce devis (la version en vigueur si un avenant a été signé), notes ajoutées après signature comprises.
        </p>
        {c.lignes.length === 0 ? (
          <p className="petit discret"><ClipboardList size={16} aria-hidden /> {c.bons ? `${pluriel(c.bons, 'bon signé')} lié${c.bons > 1 ? 's' : ''} à ce devis, sans ajout ni déduction.` : 'Aucun bon d’intervention signé lié à ce devis pour l’instant.'}</p>
        ) : (
          <>
            <ul className="bi-constates" aria-label="Ajouts et déductions constatés">
              {c.lignes.map((l, i) => (
                <li key={`${l.bon_id}-${i}`} className="bi-constate">
                  <span>
                    <Link to={`/interventions/bons/${l.bon_id}`}>{l.reference}{l.version_no > 1 ? ` v${l.version_no}` : ''}</Link>
                    <span className="petit discret"> — {date(l.le)} — {LIBELLES_SENS_AJUSTEMENT[l.sens]}{l.origine === 'note' ? ' (note après signature)' : ''}</span>
                    <span className="petit" style={{ display: 'block' }}>{l.raison}</span>
                  </span>
                  <span className="bi-constate-montant">{formatMontantSigne(l.centimes)}</span>
                </li>
              ))}
            </ul>
            <p className="bi-ajustements-total"><strong>Total net : {formatMontantSigne(c.total_net_centimes)}</strong>
              <span className="petit discret"> — {pluriel(c.lignes.length, 'ligne')}, {pluriel(c.bons, 'bon signé')}</span></p>
          </>
        )}
      </div>
    </Panneau>
  );
}
